/** User-facing copy when the device is offline. */
export const OFFLINE_TELEMETRY_BADGE = "Device offline";
export const OFFLINE_TELEMETRY_HINT =
  "Device is offline. Readings are not updating live.";
export const OFFLINE_TELEMETRY_FOOTER = "Last reading";

export function normalizeMqttId(id) {
  return String(id ?? "").trim().toLowerCase();
}

export function mqttIdsEqual(left, right) {
  const a = normalizeMqttId(left);
  const b = normalizeMqttId(right);
  return Boolean(a) && a === b;
}

export function lookupLiveOnline(liveByMqttId, mqttId) {
  if (!liveByMqttId || !mqttId) return undefined;
  const direct = liveByMqttId[mqttId];
  if (direct === true || direct === false) return direct;
  const normalized = normalizeMqttId(mqttId);
  if (!normalized) return undefined;
  if (liveByMqttId[normalized] === true || liveByMqttId[normalized] === false) {
    return liveByMqttId[normalized];
  }
  const match = Object.entries(liveByMqttId).find(
    ([key, value]) =>
      normalizeMqttId(key) === normalized && (value === true || value === false),
  );
  return match ? match[1] : undefined;
}

/** Pass through the stored online flag: true, false, or null (unknown). */
export function resolveRoleDisplayOnline(online) {
  if (online === true || online === "true" || online === "t" || online === 1 || online === "1") {
    return true;
  }
  if (
    online === false ||
    online === "false" ||
    online === "f" ||
    online === 0 ||
    online === "0"
  ) {
    return false;
  }
  return null;
}

export function resolvePairDisplayOnline(masterOnline, slaveOnline) {
  return {
    masterOnline: resolveRoleDisplayOnline(masterOnline),
    slaveOnline: resolveRoleDisplayOnline(slaveOnline),
  };
}

/** Box is online if any unit is actually online. */
export function isBoxOffline(
  masterOnline,
  slaveOnline,
  hasDevice = true,
) {
  if (!hasDevice) return false;
  if (masterOnline === true || slaveOnline === true) return false;
  return masterOnline === false || slaveOnline === false;
}

function rowHasAssignedDevice(row) {
  if (Number(row?.deviceCount ?? 0) > 0) return true;
  const device = row?.device;
  if (device?.id || device?.srId) return true;
  const legacy = row?.devices;
  if (Array.isArray(legacy) && legacy.length) return true;
  return false;
}

export function resolveLocationTelemetryLive(row) {
  const hasDevice = rowHasAssignedDevice(row);
  const masterOnline = row?.masterOnline ?? row?.master_online ?? null;
  const slaveOnline = row?.slaveOnline ?? row?.slave_online ?? null;

  if (!hasDevice) {
    return { isTelemetryLive: false, isBoxOffline: false, hasDevice: false };
  }

  const pair = resolvePairDisplayOnline(masterOnline, slaveOnline);
  const offline = isBoxOffline(pair.masterOnline, pair.slaveOnline, true);

  return {
    isTelemetryLive: !offline,
    isBoxOffline: offline,
    hasDevice: true,
    masterOnline: pair.masterOnline,
    slaveOnline: pair.slaveOnline,
  };
}

export function resolveDeviceTelemetryLive(device) {
  if (!device) {
    return { isTelemetryLive: false, isBoxOffline: false, hasDevice: false };
  }

  const masterOnline = device.masterOnline ?? null;
  const slaveOnline = device.slaveOnline ?? null;
  const pair = resolvePairDisplayOnline(masterOnline, slaveOnline);
  const offline = isBoxOffline(pair.masterOnline, pair.slaveOnline, true);

  return {
    isTelemetryLive: !offline,
    isBoxOffline: offline,
    hasDevice: true,
    masterOnline: pair.masterOnline,
    slaveOnline: pair.slaveOnline,
  };
}
